import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { get, post, errMsg, inr, dt, label } from "@/lib/api";
import { PageHeader, Kpi, Card, StatusBadge, Btn, Table, Loading, useFetch } from "@/components/kit";
import { OUTCOMES } from "@/pages/Leads";

export function Followups() {
  const nav = useNavigate();
  const [due, setDue] = useState("today");
  const { data } = useFetch(() => get("/followups", { due }), [due]);
  return (
    <div className="space-y-4">
      <PageHeader title="Follow-ups" subtitle="Your scheduled follow-ups." />
      <div className="flex gap-2">
        {["today", "overdue", "upcoming", "all"].map((d) => (
          <button key={d} data-testid={`followup-tab-${d}`} onClick={() => setDue(d)} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${due === d ? "border-blue-900 bg-blue-900 text-white" : "bg-white"}`}>
            {label(d)} {data?.counts?.[d] !== undefined && <span className="ml-1 opacity-70">({data.counts[d]})</span>}
          </button>
        ))}
      </div>
      {!data ? <Loading /> : <Table testid="followups-table" rows={data.items} onRow={(l) => nav(`/crm/leads/${l.id}`)} cols={[
        { k: "lead_code", h: "Lead", r: (l) => <span className="font-mono text-xs">{l.lead_code}</span> }, { k: "customer_name", h: "Customer" }, { k: "mobile", h: "Mobile" },
        { k: "status", h: "Status", r: (l) => <StatusBadge value={l.status} /> }, { k: "last_outcome", h: "Last outcome", r: (l) => label(l.last_outcome) },
        { k: "next_followup", h: "Due", r: (l) => dt(l.next_followup) }, { k: "assigned_employee_name", h: "Assigned" },
      ]} />}
    </div>
  );
}

function Dispose({ lead, onDone }) {
  const [f, setF] = useState({ outcome: "CONNECTED", notes: "", next_followup_at: "" });
  const save = async () => { try { await post(`/leads/${lead.id}/followups`, { type: "CALL", ...f }); toast.success("Disposition saved"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-3 grid gap-2 rounded-lg bg-slate-50 p-3 md:grid-cols-4" data-testid="disposition-form">
      <select data-testid="disposition-outcome" className="fo-input" value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })}>{OUTCOMES.map((o) => <option key={o} value={o}>{label(o)}</option>)}</select>
      <input className="fo-input" type="datetime-local" value={f.next_followup_at} onChange={(e) => setF({ ...f, next_followup_at: e.target.value })} />
      <input className="fo-input" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <Btn data-testid="disposition-save" onClick={save}>Save disposition</Btn>
    </div>
  );
}

export function Telecalling() {
  const q = useFetch(() => get("/telecalling/queue"), []);
  const s = useFetch(() => get("/telecalling/stats"), []);
  const [active, setActive] = useState(null);
  const call = async (l) => {
    try { const r = await post("/telecalling/call", { lead_id: l.id }); toast.success(`Call initiated via ${r.data.provider}`); setActive(l.id); } catch (e) { toast.error(errMsg(e)); }
  };
  const refresh = () => { setActive(null); q.reload(); s.reload(); };
  return (
    <div className="space-y-6">
      <PageHeader title="Telecalling" subtitle="Lead queue, click-to-call (pluggable cloud telephony - currently MOCK) and dispositions." />
      {s.data && <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
        <Kpi testid="tc-target" label="Daily target" value={s.data.target} /><Kpi testid="tc-calls" label="Calls today" value={s.data.calls} />
        <Kpi label="Connected" value={s.data.connected} /><Kpi label="Interested" value={s.data.interested} tone="text-emerald-700" />
        <Kpi label="Connect rate" value={`${s.data.connect_rate}%`} /><Kpi label="Conversion" value={`${s.data.conversion}%`} />
      </div>}
      <Card title={`Calling queue (${q.data?.length || 0})`}>
        {!q.data ? <Loading /> : (
          <div className="space-y-2" data-testid="calling-queue">
            {q.data.map((l) => (
              <div key={l.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><p className="font-semibold">{l.customer_name} <span className="font-mono text-xs text-slate-400">{l.lead_code}</span></p><p className="text-xs text-slate-500">{l.mobile} · {l.product} · {inr(l.loan_amount)} · Score {l.score} · Next {dt(l.next_followup)}</p></div>
                  <div className="flex items-center gap-2"><StatusBadge value={l.status} /><Btn data-testid={`call-${l.lead_code}`} variant="success" className="h-8 text-xs" onClick={() => call(l)}><Phone className="h-3.5 w-3.5" />Call</Btn></div>
                </div>
                {active === l.id && <Dispose lead={l} onDone={refresh} />}
              </div>
            ))}
            {q.data.length === 0 && <p className="text-sm text-slate-400">Queue is empty.</p>}
          </div>
        )}
      </Card>
    </div>
  );
}
